import {useEffect, useState} from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { SearchCodeModalForm } from "src/components/common/form/search/SearchCodeModalForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { SearchYearSingleForm } from "src/components/common/form/search/SearchYearSingleForm";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { CompanyDto } from "src/dtos/company/companyDto";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { WorkPlaceDto } from "src/dtos/workplace/workPlaceDto";
import { defaultPRD5020_R01Filter, usePRD5020_R01Store } from "./Store_PRD5020_R01";


// PRD5020_R01 화면 상단 검색조건 영역
export const SearchSection_PRD5020_R01 = () => {
	// 스토어에서 현재 필터값과 필터 변경 함수를 가져옴
	const { PRD5020_R01Filter, setPRD5020_R01Filter } = usePRD5020_R01Store();
	// 공통코드 스토어에서 사업장/공정/품목그룹 드롭다운 목록을 가져옴
	const { compCdList,  itemGroupList, workPlaceList, fetchWorkPlaceCdCode } = useCommonCodeStore();
	// 품목코드 검색 팝업을 열기 위한 전역 UI 상태 세터
	const { setCodeSearchModalProps } = useUiuxStore();

    useEffect(() => {
        if (workPlaceList.length === 0) {
            fetchWorkPlaceCdCode().then(); // 공정 코드 목록을 별도로 조회
        }
    }, [workPlaceList.length, fetchWorkPlaceCdCode]);

	// 품목코드 입력창에 포커스를 되돌리기 위한 식별자
	const itemCdHandleFocusId = 'prd5020_itemCd_search';

	// 품목코드 팝업 검색 필드 설정 (검색 함수, 응답 매핑, 추가 필터 등)
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'itemCd', // 대상 필드명
			category: '품목',     // 팝업 카테고리명
			focusId: itemCdHandleFocusId, // 팝업 종료 후 포커스를 돌려줄 대상
			responseMapping: {
				itemCd: 'itemCd', // 팝업 응답의 itemCd -> 필터의 itemCd
				itemNm: 'itemNm', // 팝업 응답의 itemNm -> 필터의 itemNm
			},
			// 사용자가 품목코드를 직접 타이핑하고 엔터를 눌렀을 때 유효성 확인용 검색 함수
			searchFunction: async (searchText: string) => {
				const itemResponse = await getItemPopupList(
					searchText,                                   // 검색어(품목코드)
					'all',                                         // 사업장 전체
					PRD5020_R01Filter.itemGroup || 'all',           // 현재 선택된 품목그룹으로 좁혀서 검색
					'all',                                         // 기타 조건 전체
				);
				return itemResponse.data.data.find((value) => value.itemCd === searchText);
			},
			// 팝업을 열 때 현재 필터값을 팝업 초기값으로 전달
			extraFilter: () => ({
				itemCd: PRD5020_R01Filter.itemCd || '',
				itemNm: PRD5020_R01Filter.itemNm || '',
				itemGroup: PRD5020_R01Filter.itemGroup || 'all',
			}),
		},
	];

	// 검색조건 입력 이벤트(변경/더블클릭/엔터) 핸들러들을 일괄 생성하는 훅
	const { getFieldHandlers } = useInfoFormEvents({
		editData: PRD5020_R01Filter,        // 현재 편집 중인 데이터(필터)
		setEditData: setPRD5020_R01Filter,  // 데이터 변경 함수
		defaultData: defaultPRD5020_R01Filter, // 초기화 시 되돌아갈 기본값
		infoFieldConfigs,                   // 위에서 정의한 팝업 검색 필드 설정
		setCodeSearchModalProps,            // 팝업 오픈 제어 함수
		formName: 'PRD5020_R01_search',     // 폼 식별용 이름
	});

	// 연도 피커 팝업의 표시 여부 상태
	const [yearVisible, setYearVisible] = useState(false);
	// 연도 피커 팝업의 화면상 위치(top/left) 상태
	const [yearPos, setYearPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

	return (
		<SearchSectionForm>
			{/* 사업장 드롭다운 */}
			<SearchDropDownForm<CompanyDto>
				label="사업장"
				value={PRD5020_R01Filter.compCd}
				optionList={compCdList}
				onChangeValue={(v) => setPRD5020_R01Filter({ compCd: v === 'all' ? '' : v })}
			/>

			{/* 조회 연도 선택 (단일 연도 피커) */}
			<SearchYearSingleForm
				label="연도"
				visible={yearVisible}
				setVisible={setYearVisible}
				value={new Date(PRD5020_R01Filter.year, 0, 1)}
				onChange={(date) =>
					setPRD5020_R01Filter({ year: (date ?? new Date()).getFullYear() })
				}
				pickerPosition={yearPos}
				setPickerPosition={setYearPos}
			/>

			{/* 공정 드롭다운 */}
			<SearchDropDownForm<WorkPlaceDto>
				label="공정"
				value={PRD5020_R01Filter.workplaceCd}
				optionList={workPlaceList}
				onChangeValue={(v) => setPRD5020_R01Filter({ workplaceCd: v === 'all' ? '' : v })}
			/>

			{/* 품목그룹 드롭다운 */}
			<SearchDropDownForm<SysCodeDto>
				label="품목그룹"
				value={PRD5020_R01Filter.itemGroup}
				optionList={itemGroupList}
				onChangeValue={(v) => setPRD5020_R01Filter({ itemGroup: v === 'all' ? '' : v })}
			/>

			{/* 품목코드 검색 팝업 입력 폼 */}
			<SearchCodeModalForm
				label="품목코드"
				value={PRD5020_R01Filter.itemCd}
				valueLabel={PRD5020_R01Filter.itemNm}
				handleFoucsId={itemCdHandleFocusId}
				onChange={getFieldHandlers('itemCd').onChange}
				onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
				onKeyDown={getFieldHandlers('itemCd').onEnterKeyDown}
			/>
		</SearchSectionForm>
	);
};

export default SearchSection_PRD5020_R01;
